import Breadcrumb from "components/Common/Breadcrumb"
import React, { useState, useEffect } from "react"
import { FaDownload } from "react-icons/fa"
import {
  Button,
  Card,
  CardBody,
  Modal,
  ModalBody,
  ModalHeader,
  Table,
} from "reactstrap"
import { DatePicker, message } from "antd"
import moment from "moment"
import report from "../assets/images/report.jpg"
import { fetchData, FetchPostData } from "utils/Common_api_functions"
import Swal from "sweetalert2"
import ExportModal from "./ExportModal"
import { usePermissionData } from "AppContext"

const transformPayrollData = payroll => {
  if (!payroll?.locationBreakdown) return { locations: [], rows: [] }

  const locations = payroll.locationBreakdown.map(loc => ({
    id: loc.locationId,
    name: loc.locationName,
  }))

  const dateMap = {}

  payroll.locationBreakdown.forEach(location => {
    location.timesheets.forEach(ts => {
      if (!dateMap[ts.date]) {
        dateMap[ts.date] = {
          date: ts.date,
          locations: {},
          totalHours: 0,
          totalPay: 0,
          totalHoursFormatted: "0h 0m",
        }
      }

      // ✅ Initialize array for location if not exists
      if (!dateMap[ts.date].locations[location.locationId]) {
        dateMap[ts.date].locations[location.locationId] = []
      }

      // ✅ Push each timesheet
      dateMap[ts.date].locations[location.locationId].push({
        checkIn: ts.inTime,
        checkOut: ts.outTime,
        hours: Number(ts.hours),
        hoursFormatted: ts.hoursFormatted || "",
        pay: Number(ts.pay),
      })

      dateMap[ts.date].totalHours += Number(ts.hours)
      dateMap[ts.date].totalPay += Number(ts.pay)
    })
  })

  // Format total hours
  Object.values(dateMap).forEach(row => {
    const totalMinutes = Math.round(row.totalHours * 60)
    const h = Math.floor(totalMinutes / 60)
    const m = totalMinutes % 60
    row.totalHoursFormatted = `${h}h ${m}m`
  })

  return {
    locations,
    rows: Object.values(dateMap),
  }
}

const { RangePicker } = DatePicker

const PayrollReport = () => {
  const [selectedEmployee, setSelectedEmployee] = useState("")
  const [payrollMethod, setPayrollMethod] = useState("")
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedRange, setSelectedRange] = useState([])
  const [allData, setAllData] = useState({})
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10
  const [loading, setLoading] = useState(false)
  const [exportModal, setExportModal] = useState(false)
  const [employees, setEmployees] = useState([])

  const [tableLocations, setTableLocations] = useState([])
  const [tableRows, setTableRows] = useState([])

  /* ===================== EMPLOYEES ===================== */
  useEffect(() => {
    getEmployees()
  }, [])

  const getEmployees = async () => {
    const APIdata = await fetchData(setLoading, "employee")
    if (APIdata?.success) setEmployees(APIdata.data)
    else
      Swal.fire(
        "Error",
        APIdata?.message || "Failed to load employees",
        "error"
      )
  }

  /* ===================== PAYROLL FETCH ===================== */
  useEffect(() => {
    const fetchPayrollData = async () => {
      if (!selectedEmployee || !payrollMethod) {
        setAllData([])
        return
      }

      let startDate = null
      let endDate = null

      // ✅ RANGE FIRST
      if (
        ["weekly", "15days", "fortnightly"].includes(payrollMethod) &&
        selectedRange?.length === 2
      ) {
        startDate = moment(selectedRange[0].toDate()).startOf("day")
        endDate = moment(selectedRange[1].toDate()).endOf("day")
      }

      // DAY
      else if (payrollMethod === "day" && selectedDate) {
        startDate = moment(selectedDate.toDate()).startOf("day")
        endDate = moment(selectedDate.toDate()).endOf("day")
      }

      // MONTH
      else if (payrollMethod === "monthly" && selectedDate) {
        startDate = moment(selectedDate.toDate()).startOf("month")
        endDate = moment(selectedDate.toDate()).endOf("month")
      }

      if (!startDate || !endDate) {
        setAllData({})
        return
      }

      try {
        setLoading(true)

        const res = await FetchPostData(setLoading, "payroll/of/employee", {
          employeeId: selectedEmployee,
          fromDate: startDate.format("YYYY-MM-DD"),
          toDate: endDate.format("YYYY-MM-DD"),
        })

        const payroll = res?.data?.payroll

        const { locations, rows } = transformPayrollData(payroll)

        setTableLocations(locations)
        setTableRows(rows)

        setAllData(res?.payroll || [])
        setCurrentPage(1)
      } catch (err) {
        console.error(err)
        setAllData({})
      } finally {
        setLoading(false)
      }
    }

    fetchPayrollData()
  }, [selectedEmployee, payrollMethod, selectedDate, selectedRange])

  /* ===================== DATE HANDLERS ===================== */
  const handleDayChange = date => {
    setSelectedDate(date)
    setSelectedRange([])
  }

  const handleRangeChange = dates => {
    if (!dates || dates.length !== 2) return setSelectedRange([])

    const diff = dates[1].diff(dates[0], "days") + 1
    const limit =
      payrollMethod === "weekly" ? 7 : payrollMethod === "15days" ? 15 : 14

    if (diff > limit) {
      message.error(`Range cannot exceed ${limit} days`)
      return
    }

    setSelectedRange(dates)
    setSelectedDate(null)
  }

  /* ===================== PAGINATION ===================== */
  const totalPages = Math.ceil(
    allData?.locationBreakdown?.length / itemsPerPage
  )
  const indexOfFirstItem = (currentPage - 1) * itemsPerPage
  const paginatedData = allData?.locationBreakdown?.slice(
    indexOfFirstItem,
    indexOfFirstItem + itemsPerPage
  )

  const { checkAccess } = usePermissionData()

  return (
    <div className="page-content">
      <Breadcrumb
        title="Dashboard"
        breadcrumbItem="Payroll Report"
        description="Check the Payroll of Individual Employees based on daily logs"
      />

      <Card>
        <CardBody>
          <div className="d-flex justify-content-between gap-3 flex-wrap align-items-center mb-3">
            <h3>
              {selectedEmployee
                ? `${
                    employees?.find(item => item?.id == selectedEmployee)?.name
                  }'s`
                : ""}{" "}
              Payroll Report
            </h3>

            <div className="d-flex gap-2  align-items-center">
              <Button className="btn-blue" onClick={() => setExportModal(true)}>
                <FaDownload className="me-2" /> Export
              </Button>

              <select
                className="form-select w-auto"
                value={selectedEmployee}
                onChange={e => setSelectedEmployee(e.target.value)}
              >
                <option value="">Select Employee</option>
                {employees.map(emp => (
                  <option key={emp} value={emp?.id}>
                    {emp?.name}
                  </option>
                ))}
              </select>

              <select
                className="form-select w-auto"
                value={payrollMethod}
                onChange={e => {
                  setPayrollMethod(e.target.value)
                  setSelectedDate(null)
                  setSelectedRange([])
                }}
              >
                <option value="">Select Payroll Method</option>
                <option value="day">Day</option>
                <option value="weekly">Week</option>
                <option value="15days">15 Days</option>
                <option value="fortnightly">Fortnightly</option>
                <option value="monthly">Monthly</option>
              </select>

              {payrollMethod === "day" && (
                <DatePicker
                  value={selectedDate}
                  onChange={handleDayChange}
                  format="DD-MM-YYYY"
                  placeholder="Select Day"
                />
              )}

              {["weekly", "15days", "fortnightly"].includes(payrollMethod) && (
                <RangePicker
                  value={selectedRange}
                  onChange={handleRangeChange}
                  format="DD-MM-YYYY"
                  placeholder={["Start Date", "End Date"]}
                />
              )}

              {payrollMethod === "monthly" && (
                <DatePicker
                  picker="month"
                  value={selectedDate}
                  onChange={handleDayChange}
                  format="MMM YYYY"
                  placeholder="Select Month"
                />
              )}
            </div>
          </div>

          {selectedEmployee &&
          payrollMethod &&
          (selectedDate || selectedRange.length === 2) ? (
            <Table bordered responsive hover className="mt-3">
              <thead className="table-light">
                <tr>
                  <th rowSpan="2">#</th>
                  <th rowSpan="2">Date</th>

                  {tableLocations.map(loc => (
                    <th key={loc.id} colSpan={3} className="text-center">
                      <h6 className="mb-0">{loc.name}</h6>
                    </th>
                  ))}

                  <th rowSpan="2">Total Hours</th>
                  <th rowSpan="2">Total Payment</th>
                </tr>

                <tr>
                  {tableLocations.map(loc => (
                    <React.Fragment key={loc.id}>
                      <th>Punch In - Out</th>
                      <th>Earning</th>
                      <th>Hrs</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableRows.length ? (
                  tableRows.map((row, index) => (
                    <tr key={row.date}>
                      <td>{index + 1}</td>
                      <td>{moment(row.date).format("DD MMM YYYY")}</td>

                      {tableLocations.map(loc => {
                        const locEntries = row.locations[loc.id]

                        return (
                          <React.Fragment key={loc.id}>
                            <td className="">
                              {locEntries
                                ? locEntries.map((e, i) => (
                                    <div key={i}>
                                      {e.checkIn} - {e.checkOut}
                                    </div>
                                  ))
                                : "-"}
                            </td>

                            <td>
                              {locEntries
                                ? locEntries.map((e, i) => (
                                    <div key={i}>£ {e.pay.toFixed(2)}</div>
                                  ))
                                : "-"}
                            </td>

                            <td>
                              {locEntries
                                ? locEntries.map((e, i) => (
                                    <div key={i}>{e.hoursFormatted}</div>
                                  ))
                                : "-"}
                            </td>
                          </React.Fragment>
                        )
                      })}

                      <td>{row.totalHoursFormatted}</td>
                      <td>£ {row.totalPay.toFixed(2)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={2 + tableLocations.length * 4 + 2}
                      className="text-center"
                    >
                      No data for selected period
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          ) : (
            <div className="text-center py-5 text-muted fw-semibold">
              <img
                src={report}
                className="object-fit-cover mb-2"
                alt="report"
              />
              <p className="fs-6 mb-2 text-dark">Reports are Ready</p>
              <p className="fs-5 fw-bold text-dark">
                Select Employee and Date/Range to view report
              </p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-end mt-3 gap-2">
              <Button
                size="sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => prev - 1)}
              >
                Prev
              </Button>
              {Array.from({ length: totalPages }, (_, i) => (
                <Button
                  key={i}
                  size="sm"
                  color={currentPage === i + 1 ? "primary" : "light"}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </Button>
              ))}
              <Button
                size="sm"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => prev + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </CardBody>
      </Card>

      <ExportModal exportModal={exportModal} setExportModal={setExportModal} />
    </div>
  )
}

export default PayrollReport
